import React, { useMemo, useState, useEffect } from "react";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import { CreateDirectDiscountApi } from "../../ApiRequests";
import AuthBtn from "../Buttons/AuthBtn";
import { toLocalYmd, toUnixSeconds } from "../../utils/dateTime";

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const AddDirectDiscountModal = ({
  Open,
  setOpen,
  userType,
  entity,
  onSuccess,
}) => {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [dateStr, setDateStr] = useState(() => toLocalYmd());
  const [loading, setLoading] = useState(false);

  const isCustomer = Number(userType) === 1;

  /** Current outstanding balance (customer receivable / company payable). */
  const currentBalance = useMemo(() => {
    if (!entity) return 0;
    if (isCustomer) return Number(entity.recieveable || 0);
    return Number(entity.payable || 0);
  }, [entity, isCustomer]);

  const discountAmt = Number(amount);
  const hasValidDiscount = Number.isFinite(discountAmt) && discountAmt > 0;
  const balanceAfterDiscount = hasValidDiscount
    ? currentBalance - discountAmt
    : currentBalance;

  useEffect(() => {
    if (Open) {
      setAmount("");
      setDesc("");
      setDateStr(toLocalYmd());
    }
  }, [Open, entity?._id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!entity?._id) {
      WarningToast("No record selected");
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      WarningToast("Enter a valid discount amount");
      return;
    }

    const payload = {
      user_type: userType,
      amount: amt,
      desc: desc.trim(),
      date: toUnixSeconds(dateStr),
    };
    if (isCustomer) payload.customerId = entity._id;
    else payload.companyId = entity._id;

    setLoading(true);
    try {
      const res = await CreateDirectDiscountApi(payload);
      if (res.data.success) {
        SuccessToast("Discount saved");
        setOpen(false);
        if (typeof onSuccess === "function") onSuccess();
      } else {
        ErrorToast(res.data.error?.msg || "Failed");
      }
    } catch (err) {
      ErrorToast(
        err.response?.data?.error?.msg ||
          err.message ||
          "Failed to save discount"
      );
    }
    setLoading(false);
  };

  const title = isCustomer ? "Add customer discount" : "Add company discount";
  const balanceLabel = isCustomer ? "Current receivable" : "Current payable";

  return (
    <CustomModal open={!!Open} setOpen={setOpen}>
      <div className="w-[min(92vw,440px)] pb-8 font-quicksand">
        <h1 className="w-full text-center font-[700] text-2xl py-6 px-4 text-ink">
          {title}
        </h1>
        <p className="text-center text-sm text-inkSecondary px-6 mb-3">
          {entity?.name || "—"}
        </p>

        <div className="mx-8 mb-4 rounded-xl border border-line bg-subtle/60 px-4 py-3 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted font-semibold">{balanceLabel}</span>
            <span className="font-bold tabular-nums text-ink">
              {formatRs(currentBalance)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted font-semibold">After discount</span>
            <span
              className={`font-bold tabular-nums ${
                hasValidDiscount ? "text-main" : "text-ink"
              }`}
            >
              {formatRs(balanceAfterDiscount)}
            </span>
          </div>
          {hasValidDiscount && (
            <p className="text-[11px] text-muted pt-1 border-t border-line/80">
              Discount {formatRs(discountAmt)} will reduce the balance by that
              amount.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-y-3 px-8">
          <AuthInput
            label="Discount amount (PKR)"
            placeholder="Amount"
            Type="number"
            Value={amount}
            setValue={setAmount}
            Width="w-full"
          />
          <AuthInput
            label="Description"
            placeholder="Optional note"
            Value={desc}
            setValue={setDesc}
            Width="w-full"
          />
          <AuthInput
            label="Date"
            Type="date"
            Value={dateStr}
            setValue={setDateStr}
            Width="w-full"
          />
        </div>
        <div className="flex justify-center items-center gap-x-2 mt-6 px-5">
          {loading ? (
            <AddingLightLoader />
          ) : (
            <>
              <AuthBtn title={"Save discount"} onSubmit={onSubmit} type={1} />
              <AuthBtn
                title={"Cancel"}
                onSubmit={() => setOpen(false)}
                type={2}
              />
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default AddDirectDiscountModal;
